import {
  getAnalytics,
  setAnalyticsConfig,
} from "@libertify.frontend/analytics";
import {
  ANALYTICS_TEXT_VERSION,
  CONSENT_MAX_AGE_MS,
  getCookieConsent,
  subscribeCookieConsent,
  type ConsentValue,
} from "@libertify/ui";
import type { components } from "@libertify/api-types/api";

// GA4 Web destination that Segment loads on the production source
const GA4_MEASUREMENT_ID = "G-9G9P3XKXRG";

export type ConsentDecision = components["schemas"]["ConsentDecision"];
export type ConsentMethod = components["schemas"]["ConsentMethod"];
export type ConsentResponse = components["schemas"]["ConsentResponse"];

type RestorableConsent = { value: NonNullable<ConsentValue>; date: number };

/** The server answer the banner can reuse: same text version, not older than 6 months. */
export const getRestorableConsent = (
  records: ConsentResponse[]
): RestorableConsent | null => {
  const record = records.find(
    ({ consent_type }) => consent_type === "analytics"
  );
  if (!record || record.text_version !== ANALYTICS_TEXT_VERSION) return null;
  const date = Date.parse(record.created_at);
  if (Number.isNaN(date) || Date.now() - date > CONSENT_MAX_AGE_MS) {
    return null;
  }
  return { value: record.decision === "granted" ? "true" : "rejected", date };
};

// Google's documented opt-out flag, read by the GA4 script on each hit
const setGa4Disabled = (disabled: boolean) => {
  Reflect.set(globalThis, `ga-disable-${GA4_MEASUREMENT_ID}`, disabled);
};

const isTrackingCookie = (name: string) =>
  name.startsWith("ajs_") || name === "_ga" || name.startsWith("_ga_");

// GA4 sets its cookies on the parent domain, so expire them on every level
const deleteCookie = (name: string) => {
  const expired = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  document.cookie = expired;
  const labels = globalThis.location.hostname.split(".");
  for (let i = 0; i < labels.length - 1; i++) {
    document.cookie = `${expired}; domain=.${labels.slice(i).join(".")}`;
  }
};

const clearTrackingStorage = () => {
  Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i))
    .filter((key): key is string => key !== null)
    .filter(
      (key) => key.startsWith("ajs_") || key.startsWith("persisted-queue:")
    )
    .forEach((key) => localStorage.removeItem(key));
  document.cookie
    .split(";")
    .map((cookie) => cookie.split("=")[0]?.trim() ?? "")
    .filter(isTrackingCookie)
    .forEach(deleteCookie);
};

const resetSegment = async () => {
  try {
    const analytics = await getAnalytics();
    await analytics.reset();
  } catch {
    // Segment not configured or failed to load: nothing to reset
  }
};

/** Logout cleanup: storage is cleared synchronously, before a redirect can cut the async reset off. */
export const clearSegmentIdentity = () => {
  clearTrackingStorage();
  // Drops the previous user from Segment's memory when the page stays (no SSO redirect)
  void resetSegment();
};

const stopTracking = async () => {
  setAnalyticsConfig({ enabled: false });
  setGa4Disabled(true);
  await resetSegment();
  // The user may have accepted again while Segment was resetting
  if (getCookieConsent() === "rejected") clearTrackingStorage();
};

/** Starts or stops Segment and GA4 whenever the cookie answer changes, without reload. */
export const syncAnalyticsWithConsent = () =>
  subscribeCookieConsent(() => {
    const consent = getCookieConsent();
    if (consent === "true") {
      setGa4Disabled(false);
      setAnalyticsConfig({ enabled: true });
    } else if (consent === "rejected") {
      void stopTracking();
    }
  });
